import { Action, Store, createAction } from '@reduxjs/toolkit';
import { DocumentStates, DocumentType, FileStatus, InspectFileState } from 'types';
import { fetchReportById, createReport, uploadReport } from './report/requests';
import { fetchUserPreferences } from './auth/requests';
import { fetchAppIntegrations } from './app/requests';
import { axiosInstance } from './store';
import { pollFileStatus } from './request/files/actions';
import files from './files/files';
import { FileErrors, FileFlags } from '@verify/shared-utils';
import app from './app/app';
import { updateInspection, fetchLinkedInspections } from './request/inspections/actions';
import { QueryState } from '@redux-requests/core';
import { AxiosError, AxiosProgressEvent } from 'axios';
import inspection from './inspection/inspection';

const CANCEL_UPLOAD = 'CANCEL_UPLOAD';
const cancelUpload = createAction(CANCEL_UPLOAD);

const START_INSPECTION = 'START_INSPECTION';
const startInspection = createAction(START_INSPECTION);

const RESET_INSPECTION = 'RESET_INSPECTION';
const resetInspection = createAction(RESET_INSPECTION);

const SELECT_UNCONFIRMED_ZONES = 'SELECT_UNCONFIRMED_ZONES';
const selectUnconfirmedZones = createAction(SELECT_UNCONFIRMED_ZONES);

export const startHtmlToPdf = createAction('requests/htmlToPdf', (url: string, document: DocumentType) => {
  return {
    payload: {
      request: {
        url: '/files/html',
        method: 'POST',
        data: {
          url,
        },
      },
    },
    meta: {
      document,
      errorMessage: 'There was an error loading the file from URL',
      onSuccess: (response: any, _requestAction: Action, store: Store) => {
        const file = response?.data;
        if (file?.id) {
          store.dispatch(
            inspection.actions.setLoadingState({ documentType: document, documentState: DocumentStates.LOADING }),
          );
          store.dispatch(
            files.actions.setFile({
              fileState: {
                fileId: file.id,
                url: file.signedUrl || '',
                fileName: file.filename,
                originalName: file.originalFilename,
                status: file.status,
                createdAt: file.createdAt,
              },
              documentType: document,
              isNewFile: true,
            }),
          );
        } else {
          store.dispatch(
            inspection.actions.setLoadingState({ documentType: document, documentState: DocumentStates.INITIAL }),
          );
        }
        return response;
      },
      onError: (error: AxiosError, _action: any, store: Store) => {
        store.dispatch(
          inspection.actions.setLoadingState({ documentType: document, documentState: DocumentStates.INITIAL }),
        );
      },
    },
  };
});

const startFileCreation = createAction(
  'request/startFileCreation',
  (
    data: FormData,
    document: DocumentType,
    uploadProgressUpdate: (progressEvent: AxiosProgressEvent) => void,
    removeHeadersFooters: boolean,
    autoSelectLayersSeparations: boolean,
  ) => {
    const file = data.get('file');
    const checksum = data.get('checksum') as string;
    const fileName = file instanceof File ? file.name : '';
    const fileType = file instanceof File ? file.type : '';
    const fileSize = file instanceof File ? file.size : 0;
    return {
      payload: {
        request: {
          url: `/files/startFileCreation`,
          method: 'POST',
          data: {
            fileName,
            fileType,
            checksum,
            removeHeadersFooters,
            autoSelectLayersSeparations,
          },
        },
      },
      meta: {
        document,
        errorMessage: 'There was an error uploading the file. Please try again.',
        abortOn: cancelUpload.type,
        onSuccess: (response: any, _requestAction: Action, store: Store) => {
          store.dispatch(
            inspection.actions.setLoadingState({ documentType: document, documentState: DocumentStates.LOADING }),
          );
          axiosInstance
            .put(response.data.signedURL, file, {
              headers: {
                'Content-Type': fileType,
              },
              onUploadProgress: uploadProgressUpdate,
            })
            .then((resp) => {
              if (store.getState().files.isCreationCancelled === false) {
                store.dispatch(finishFileCreation(response?.data?.id, resp.status, document, fileSize, fileType));
              }
            });

          return response;
        },
      },
    };
  },
);

const finishFileCreation = createAction(
  'request/finishFileCreation',
  (fileId: string, status: number, document: DocumentType, fileSize: number, fileType: string) => {
    return {
      payload: {
        request: {
          url: `/files/finishFileCreation/${fileId}`,
          method: 'PATCH',
          data: {
            status,
            fileMetadata: {
              fileSize,
              fileType,
            },
          },
        },
      },
      meta: {
        errorMessage: 'There was an error finishing the file creation. Please try again.',
        onSuccess: (_response: any, _requestAction: Action, store: Store) => {
          store.dispatch(files.actions.setRequestKey({ documentType: document, requestKey: fileId }));
          store.dispatch(pollFileStatus(fileId, document));
        },
        onError: (error: AxiosError, _action: any, store: Store) => {
          store.dispatch(
            inspection.actions.setLoadingState({ documentType: document, documentState: DocumentStates.INITIAL }),
          );
        },
      },
    };
  },
);

const preprocessFile = createAction('request/preProcessFile', (file: InspectFileState, documentType: DocumentType) => {
  return {
    payload: {
      request: {
        url: `/files/preprocess/${file.fileId}`,
        method: 'POST',
      },
    },
    meta: {
      errorMessage: 'There was an error loading the file. Please try again.',
      onSuccess: (response: QueryState<boolean>, _requestAction: Action, store: Store) => {
        if (response.data === true) {
          // its pending so we have to poll until it's done
          store.dispatch(files.actions.setRequestKey({ documentType, requestKey: file.fileId }));
          store.dispatch(pollFileStatus(file.fileId, documentType));
          store.dispatch(files.actions.setFileStatus({ documentType, status: FileStatus.queued }));
        } else {
          store.dispatch(files.actions.setFileStatus({ documentType, status: FileStatus.completed }));
          store.dispatch(
            files.actions.setFileFromLibrary({
              fileState: file,
              documentType,
              isNewFile: true,
            }),
          );
        }
        return response;
      },
      onError: (error: AxiosError, _action: any, store: Store) => {
        store.dispatch(store.dispatch(files.actions.setFileStatus({ documentType, status: FileStatus.error })));
      },
    },
  };
});

const fetchFileList = createAction('request/fetchFileList', () => ({
  payload: {
    request: {
      url: '/files',
      method: 'GET',
    },
  },
  meta: {
    errorMessage: 'There was an error loading the files. Please try again.',
  },
}));

const fetchFile = createAction(
  'request/fetchFile',
  (id: string, documentType: DocumentType, isNewFile: boolean, inspectionId?: string) => ({
    payload: {
      request: {
        url: inspectionId
          ? `/files/${id}?inspectionId=${inspectionId}&checkS3Exist=true`
          : `/files/${id}?checkS3Exist=true`,
        method: 'GET',
      },
    },
    meta: {
      document: documentType,
      errorMessage: 'There was an error. Please try again.',
      takeLatest: false,
      onSuccess: (response: QueryState<any>, _requestAction: Action, store: Store) => {
        const {
          signedUrl,
          id,
          filename,
          originalFilename,
          status,
          createdAt,
          mergedMetadata: mergedFileData = [],
          flags,
          error,
          fileMetadata,
        } = response.data;

        const inspectionId: string = store.getState().inspectionId;
        let fileRemovalProps;
        if (documentType === 'source') {
          fileRemovalProps = { masterFileId: null };
        } else {
          fileRemovalProps = { sampleFileId: null };
        }

        if (status === FileStatus.error) {
          if (inspectionId) {
            store.dispatch(updateInspection(inspectionId, fileRemovalProps));
          }
          error === FileErrors.AiNonCompatible || error === FileErrors.IncompatibleZipFile
            ? store.dispatch(files.actions.setIsFileErrorOpen(error))
            : store.dispatch(
                app.actions.setSnackMessage({
                  message: 'There was an error uploading the file. Please try with a different file.',
                  type: 'error',
                }),
              );

          store.dispatch(files.actions.setFileStatus({ documentType, status: FileStatus.error }));
          return response;
        }

        // S3 object is missing (entity exists but content was deleted, e.g. by retention policy).
        // Reset fileId to '' so DocumentsPanel shows UploadPage instead of loading an empty URL.
        // Also mark the document as LOADED so the results tab spinner doesn't wait indefinitely.
        const resolvedFileId = signedUrl ? id : '';
        const resolvedUrl = signedUrl ?? '';

        if (!signedUrl) {
          store.dispatch(inspection.actions.setLoadingState({ documentType, documentState: DocumentStates.LOADED }));
          store.dispatch(files.actions.setFileUnavailable({ documentType, unavailable: true }));
          store.dispatch(app.actions.addUnavailableFile(id));
        }

        store.dispatch(
          files.actions.setFetchedFile({
            fileState: {
              fileId: resolvedFileId,
              url: resolvedUrl,
              fileName: filename,
              originalName: originalFilename,
              status,
              createdAt,
              mergedFileData,
              isOCR: flags?.includes(FileFlags.ocr),
              fileMetadata,
            },
            documentType,
            isNewFile: signedUrl ? isNewFile : false,
          }),
        );
        const multipleFileStatus = store.getState().files.multipleFileStatus[documentType];
        if (multipleFileStatus) {
          // For merged docs, if status is true, we need to reset to false at this point
          store.dispatch(files.actions.setMultipleFileStatus({ documentType, status: false }));
        }

        return response;
      },
    },
  }),
);

const fetchFileStatus = createAction('request/fetchFileStatus', (id: string, documentType: DocumentType) => ({
  payload: {
    request: {
      url: `/files/${id}`,
      method: 'GET',
    },
  },
  meta: {
    document: documentType,
    errorMessage: 'There was an error. Please try again.',
    takeLatest: false,
  },
}));

export {
  finishFileCreation,
  startFileCreation,
  fetchFileList,
  fetchFile,
  cancelUpload,
  startInspection,
  fetchFileStatus,
  resetInspection,
  fetchReportById,
  createReport,
  uploadReport,
  selectUnconfirmedZones,
  fetchUserPreferences,
  fetchAppIntegrations,
  preprocessFile,
  fetchLinkedInspections,
};
